import React from 'react';
import { useUser } from '@/contexts/UserContext';
import { addDays, subDays } from 'date-fns';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import GuestLanding from './landing/GuestLanding';
import UpcomingMeals from './landing/UpcomingMeals';
import YesterdayMeals from './landing/YesterdayMeals';
import RecentRecipes from './landing/RecentRecipes';
import RecentActivity from './landing/RecentActivity';
import WellnessWidget from './landing/WellnessWidget';
import { NewUserOnboarding } from './onboarding';
import { useCurrentUserFamily } from '@/hooks/useCurrentUserFamily';
import { useIsNewUser } from '@/hooks/useIsNewUser';
import { useMealPlans } from '@/hooks/useMealPlans';
import { useRecipes } from '@/hooks/useRecipes';
import { useMealCompositions } from '@/hooks/useMealCompositions';
import { useMealNotes } from '@/hooks/useMealNotes';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { getStandardTopPadding, getStandardBottomPadding } from '@/lib/layout-utils';

export default function LandingPage() {
  const { isAuthenticated, user } = useUser();
  const { isNewUser, isLoading: newUserLoading } = useIsNewUser();
  const isMobile = useIsMobile();
  
  const today = new Date();
  const yesterday = subDays(today, 1);
  const tomorrow = addDays(today, 1);
  
  // Use the updated useCurrentUserFamily hook without familyName
  const { familyId, isLoading: familyLoading } = useCurrentUserFamily();
  
  // Fetch data with standard React Query hooks
  const { data: dailyMealPlans = [], isLoading: mealPlansLoading, refetch: refetchMealPlans } = useMealPlans(yesterday, tomorrow, familyId);
  const { data: recipes = [], isLoading: recipesLoading, refetch: refetchRecipes } = useRecipes();
  const { data: mealCompositions = [], isLoading: compositionsLoading, refetch: refetchCompositions } = useMealCompositions();
  const { data: mealNotes = [], isLoading: mealNotesLoading, refetch: refetchMealNotes } = useMealNotes(yesterday, tomorrow, familyId);

  if (!isAuthenticated) {
    return <GuestLanding />;
  }

  // Show onboarding for new users (users with no recipes)
  if (isNewUser && !newUserLoading) {
    return <NewUserOnboarding />;
  }

  // Show loading state while checking if user is new
  if (newUserLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Use standardized padding calculations
  const topPadding = getStandardTopPadding();
  const bottomPadding = getStandardBottomPadding({ isAuthenticated, isMobile });

  // Show regular landing page for existing users
  return (
    <div className={cn(
      "min-h-screen bg-background",
      topPadding,
      bottomPadding,
      "md:pb-8"
    )}>
      
      <div className="container px-3 md:px-4 mx-auto max-w-6xl">
        <main className="mb-8">
          <div className="flex flex-col">
            
            {/* Mobile Layout - Single column with current order */}
            <div className="lg:hidden space-y-6">
              {/* Upcoming Meals - First on mobile */}
              <UpcomingMeals 
                mealPlans={dailyMealPlans} 
                recipes={recipes}
                mealCompositions={mealCompositions}
                mealNotes={mealNotes}
                isLoading={mealPlansLoading || recipesLoading || compositionsLoading || mealNotesLoading || familyLoading}
              />

              {/* Recent Activity - Second on mobile */}
              <div>
                <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-charcoal font-playfair mb-4 mt-2">Recent Activity</h2>
                <RecentActivity />
                <div className="mt-4 flex justify-end">
                  <Link to="/family-activity">
                    <Button variant="ghost" size="sm" className="flex items-center">
                      View All Activity
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </div>

              {/* Wellness Widget - Third on mobile */}
              <div>
                <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-charcoal font-playfair mb-4 mt-2">Wellness snapshot</h2>
                <WellnessWidget />
                <div className="mt-4 flex justify-end">
                  <Link to="/wellness">
                    <Button variant="ghost" size="sm" className="flex items-center">
                      View Full Wellness Report
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </div>

              {/* Yesterday's Meals - Fourth on mobile */}
              <YesterdayMeals 
                mealPlans={dailyMealPlans} 
                recipes={recipes}
                mealCompositions={mealCompositions}
                mealNotes={mealNotes}
                isLoading={mealPlansLoading || recipesLoading || compositionsLoading || mealNotesLoading || familyLoading}
              />

              {/* Recent Recipes - Fifth on mobile */}
              <RecentRecipes 
                recipes={recipes} 
                isLoading={recipesLoading} 
              />
            </div>

            {/* Desktop Layout - Two columns with vertical stacking */}
            <div className="hidden lg:grid lg:grid-cols-2 gap-8">
              {/* Left Column - Meal-related widgets */}
              <div className="space-y-8">
                {/* Upcoming Meals - First in left column */}
                <UpcomingMeals 
                  mealPlans={dailyMealPlans} 
                  recipes={recipes}
                  mealCompositions={mealCompositions}
                  mealNotes={mealNotes}
                  isLoading={mealPlansLoading || recipesLoading || compositionsLoading || mealNotesLoading || familyLoading}
                />

                {/* Yesterday's Meals - Second in left column */}
                <YesterdayMeals 
                  mealPlans={dailyMealPlans} 
                  recipes={recipes}
                  mealCompositions={mealCompositions}
                  mealNotes={mealNotes}
                  isLoading={mealPlansLoading || recipesLoading || compositionsLoading || mealNotesLoading || familyLoading}
                />

                {/* Recent Recipes - Third in left column */}
                <RecentRecipes 
                  recipes={recipes} 
                  isLoading={recipesLoading} 
                />
              </div>

              {/* Right Column - Activity widgets */}
              <div className="space-y-8">
                {/* Recent Activity - First in right column */}
                <div>
                  <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-charcoal font-playfair mb-4 mt-2">Recent Activity</h2>
                  <RecentActivity />
                  <div className="mt-4 flex justify-end">
                    <Link to="/family-activity">
                      <Button variant="ghost" size="sm" className="flex items-center">
                        View All Activity
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </div>

                {/* Wellness Widget - Second in right column */}
                <div>
                  <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-charcoal font-playfair mb-4 mt-2">Wellness snapshot</h2>
                  <WellnessWidget />
                  <div className="mt-4 flex justify-end">
                    <Link to="/wellness">
                      <Button variant="ghost" size="sm" className="flex items-center">
                        View Full Wellness Report
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
